import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, ExternalLink, Eye, Save, RotateCcw } from "lucide-react";
import { fmtDate, fmtTime, inr } from "@/lib/tps";
import { useAuth } from "@/context/AuthContext";

function renderTemplate(body, order) {
  if (!body) return "";
  if (!order) {
    // strip leftover placeholders if no order context
    return body.replace(/\{\{?\s*(\w+)\s*\}?\}/g, "");
  }
  const it = order.items?.[0] || {};
  const date = order.required_date ? fmtDate(order.required_date) : "";
  const time = order.required_time ? fmtTime(order.required_time) : "";
  const map = {
    customer_name: order.customer_name || "",
    order_id: order.order_id || "",
    order_date: date,
    order_time: time,
    theme: it.theme || "—",
    weight: it.weight || "—",
    tier: it.tier || "—",
    flavour: it.flavour || "—",
    cake_message: it.cake_message || "—",
    customization_notes: it.customization_notes || "—",
    delivery_method: order.delivery_method || "",
    delivery_address: order.delivery_address || "—",
    total_price: order.total_price?.toLocaleString("en-IN") || "0",
    balance_amount: order.balance_amount?.toLocaleString("en-IN") || "0",
  };
  // Support both {{key}} and {key} placeholder formats
  let out = body.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => map[k] ?? "");
  out = out.replace(/\{\s*(\w+)\s*\}/g, (_, k) => map[k] ?? "");
  return out;
}

export default function Templates() {
  const { isOwner } = useAuth();
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [templates, setTemplates] = useState([]);
  const [order, setOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selOrderId, setSelOrderId] = useState(orderId || "");
  const [active, setActive] = useState("order_confirmation");
  const [editBody, setEditBody] = useState("");
  const [editName, setEditName] = useState("");

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/orders").then((r) => setOrders(r.data.slice(0, 200)));
  }, []);

  useEffect(() => {
    if (!selOrderId) { setOrder(null); return; }
    api.get(`/orders/${selOrderId}`).then((r) => setOrder(r.data)).catch(() => setOrder(null));
  }, [selOrderId]);

  useEffect(() => {
    // auto-pick template by delivery method when an order is loaded
    if (!order) return;
    if (order.delivery_method === "Uber") setActive("out_for_delivery_uber");
    else if (order.delivery_method === "Porter" || order.delivery_method === "Swiggy Genie") setActive("out_for_delivery_porter");
  }, [order]);

  const tpl = templates.find((t) => t.key === active);
  useEffect(() => {
    if (tpl) { setEditBody(tpl.body || ""); setEditName(tpl.name || ""); }
  }, [tpl?.key, tpl?.updated_at]); // eslint-disable-line

  const previewBody = useMemo(() => renderTemplate(editBody || tpl?.body || "", order), [editBody, tpl, order]);

  const previewImage = useMemo(() => {
    if (!order) return null;
    if (active === "order_confirmation") return order.final_reference_image || order.reference_image;
    if (active.startsWith("out_for_delivery")) return order.final_cake_ready_picture;
    return null;
  }, [active, order]);

  const copy = async () => {
    await navigator.clipboard.writeText(previewBody);
    toast.success("Message copied. Image must be attached separately in WhatsApp.");
  };

  const openWA = () => {
    const phone = (order?.customer_phone || "").replace(/[^\d]/g, "");
    const num = phone.startsWith("91") || !phone ? phone : `91${phone}`;
    const url = `https://wa.me/${num}?text=${encodeURIComponent(previewBody)}`;
    window.open(url, "_blank");
  };

  const saveTemplate = async () => {
    if (!isOwner) return;
    if (!editBody.trim()) {
      toast.error("Template body cannot be empty. Use Reset to restore the default.");
      return;
    }
    try {
      await api.put(`/templates/${active}`, { key: active, name: editName, body: editBody });
      toast.success("Template saved");
      const { data } = await api.get("/templates");
      setTemplates(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const resetTemplate = async () => {
    if (!isOwner) return;
    if (!window.confirm("Restore this template to the default? Your changes will be lost.")) return;
    try {
      await api.post(`/templates/${active}/reset`);
      const { data } = await api.get("/templates");
      setTemplates(data);
      toast.success("Template restored to default");
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
  };

  return (
    <div className="space-y-3 animate-fade-in" data-testid="templates-page">
      <h1 className="font-heading text-2xl md:text-3xl">WhatsApp Templates</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card className="border-[#E8E3DF]">
          <CardContent className="p-3 space-y-2">
            <div className="text-xs uppercase tracking-wider text-[#4B5563] mb-1">Templates</div>
            {templates.map((t) => (
              <button
                key={t.key} onClick={() => setActive(t.key)}
                data-testid={`tpl-${t.key}`}
                className={`w-full text-left px-3 py-2 rounded-md text-sm border ${active === t.key ? "border-[#B86F77] bg-[#F3E8E9] text-[#B86F77] font-medium" : "border-transparent hover:bg-[#FCF9F9]"}`}
              >{t.name}</button>
            ))}
            <div className="border-t border-[#E8E3DF] pt-3 mt-3">
              <div className="text-xs text-[#4B5563] mb-1">Linked Order (optional)</div>
              <Select value={selOrderId || "__none__"} onValueChange={(v) => setSelOrderId(v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-8" data-testid="tpl-order-select"><SelectValue placeholder="Pick order" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">— None —</SelectItem>
                  {orders.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.order_id} · {o.customer_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {order && (
                <Link to={`/orders/${order.id}`} className="text-xs text-[#B86F77] hover:underline block mt-1">Open order →</Link>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E8E3DF] lg:col-span-2">
          <CardContent className="p-3 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-medium">{editName}</div>
              <div className="flex gap-1.5">
                <Button onClick={copy} className="h-8 bg-[#B86F77] hover:bg-[#A35D65]" data-testid="tpl-copy-btn"><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
                <Button variant="outline" onClick={openWA} disabled={!order} className="h-8 border-[#E8E3DF]" data-testid="tpl-wa-btn"><ExternalLink className="h-3.5 w-3.5 mr-1" /> WhatsApp</Button>
                {isOwner && <Button variant="outline" onClick={resetTemplate} className="h-8 border-[#E8E3DF]" data-testid="tpl-reset-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset</Button>}
                {isOwner && <Button variant="outline" onClick={saveTemplate} className="h-8 border-[#E8E3DF]" data-testid="tpl-save-btn"><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-[#4B5563] mb-1">Message Body {!isOwner && <span>(read-only)</span>}</div>
                <Textarea
                  rows={14}
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  readOnly={!isOwner}
                  className="font-mono text-xs"
                  data-testid="tpl-body-editor"
                />
              </div>

              <div>
                <div className="text-xs text-[#4B5563] mb-1 flex items-center gap-1"><Eye className="h-3 w-3" /> Preview</div>
                <div className="bg-[#ECE5DD] rounded-lg p-3 min-h-[20rem]">
                  <div className="wa-preview" data-testid="tpl-preview">{previewBody}</div>
                  {previewImage && (
                    <div className="mt-2">
                      <img src={previewImage} alt="attachment" className="max-h-48 rounded-md border border-[#E8E3DF]" data-testid="tpl-preview-image" />
                      <div className="text-[10px] text-[#4B5563] mt-1">Will be attached in WhatsApp (you'll attach manually after copying).</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
